import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { bookingKeys } from '@/lib/queryKeys';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { apiPatch, operationsClient } from '@/lib/api';
import { mapAppointmentStatus } from './useBookings';
import type {
  Booking,
  BookingStatus,
  UpdateBookingStatusDto,
  BookingListResponse,
} from '@/types/booking';

const UNSCHEDULED_KEY = (tenantKey: readonly unknown[]) => [...tenantKey, 'unscheduled'] as const;

export function toBookingStatusResponse(
  raw: Record<string, unknown>,
  fallback: { id: string; status: BookingStatus },
): Booking {
  return {
    id: String(raw.bookingId ?? raw.id ?? fallback.id),
    jobId: typeof raw.jobId === 'string' ? raw.jobId : null,
    appointmentId:
      typeof raw.appointmentId === 'string' && raw.appointmentId.trim()
        ? raw.appointmentId.trim()
        : null,
    status: mapAppointmentStatus(String(raw.status ?? fallback.status)),
    createdAt: String(raw.createdAt ?? ''),
    updatedAt: String(raw.updatedAt ?? raw.createdAt ?? ''),
  } as Booking;
}

export function useUpdateBookingStatus() {
  const queryClient = useQueryClient();

  return useApiMutation<Booking, UpdateBookingStatusDto>({
    mutationFn: async ({ id, status }) => {
      const raw = await apiPatch<Record<string, unknown>, { status: BookingStatus }>(
        operationsClient,
        schedulerEndpoints.bookingStatus(id),
        { status },
      );
      return toBookingStatusResponse(raw, { id, status });
    },

    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: bookingKeys.all() });

      // Snapshot all list caches
      const listSnapshots = new Map<string, BookingListResponse>();
      queryClient
        .getQueriesData<BookingListResponse>({ queryKey: bookingKeys.lists() })
        .forEach(([key, data]) => {
          if (data) {
            listSnapshots.set(JSON.stringify(key), data);
            queryClient.setQueryData<BookingListResponse>(key as string[], {
              ...data,
              bookings: data.bookings.map((b) => (b.id === id ? { ...b, status } : b)),
            });
          }
        });

      // Snapshot the unscheduled query (uses a different key shape — not covered by bookingKeys.lists())
      const unscheduledKey = UNSCHEDULED_KEY(bookingKeys.all());
      const previousUnscheduled = queryClient.getQueryData<Booking[]>(unscheduledKey);
      if (previousUnscheduled) {
        queryClient.setQueryData<Booking[]>(
          unscheduledKey,
          previousUnscheduled.filter((b) => b.id !== id),
        );
      }

      // Snapshot detail cache
      const previousDetail = queryClient.getQueryData<Booking>(bookingKeys.detail(id));
      if (previousDetail) {
        queryClient.setQueryData<Booking>(bookingKeys.detail(id), { ...previousDetail, status });
      }

      return { listSnapshots, previousUnscheduled, previousDetail };
    },

    onError: (_err, { id }, context) => {
      const ctx = context as
        | {
            listSnapshots?: Map<string, BookingListResponse>;
            previousUnscheduled?: Booking[];
            previousDetail?: Booking;
          }
        | undefined;

      ctx?.listSnapshots?.forEach((data, keyStr) => {
        queryClient.setQueryData(JSON.parse(keyStr) as string[], data);
      });
      // Restore unscheduled query on failure
      if (ctx?.previousUnscheduled) {
        queryClient.setQueryData(UNSCHEDULED_KEY(bookingKeys.all()), ctx.previousUnscheduled);
      }
      if (ctx?.previousDetail) {
        queryClient.setQueryData(bookingKeys.detail(id), ctx.previousDetail);
      }
      toast.error('Failed to update status — reverted');
    },

    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: bookingKeys.all() });
      void queryClient.invalidateQueries({ queryKey: bookingKeys.detail(data.id) });
    },
  });
}
